import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SEND_RECEIPT_EXPRESS_SERVICES } from '../../../../constants/paths/api';
import { getCurrentLangPretty } from '../../../../helpers/url/urlHelpers';
import { InstructionToStaffOption } from '../../../../interfaces/sendReceipt/sendReceiptMultistep';
import { P } from '../../../base/typography';
import InactiveStep from '../../InactiveStep';
import { useMultiStepFormContext } from '../../context/multistepContext';
import { useTranslationsContext } from '../../context/translationsContext';
import { FormStepProps } from '../../sendReceiptMultistep';
import {
	ButtonWrapper,
	FormStepHeading,
	FormStepWrapper,
	PrevButton,
	ProceedButton,
} from '../../sendReceiptMultistep.styles';
import Services, { DEFAULT_DELIVERY_SERVICE } from './Services';

export const shouldShowDeliveryStep = (instructionToStaff: InstructionToStaffOption) =>
	instructionToStaff === 'hasInvoice';
export const DELIVERY_STEP_INDEX = 4;

export default function Delivery({ values, touched, errors, setFieldValue }: FormStepProps) {
	const translations = useTranslationsContext();
	const { currentStepIndex, latestStepIndexVisited, next, prev, goTo } = useMultiStepFormContext();
	const { t } = useTranslation('sendReceiptMultistep');
	const lang = getCurrentLangPretty();
	const defaultService = DEFAULT_DELIVERY_SERVICE[lang];
	const [previousIdentificationNumber, setPreviousIdentificationNumber] = useState('');
	const [previousTrackingNumbers, setPreviousTrackingNumbers] = useState(0);
	const [deliveryServices, setDeliveryServices] = useState([defaultService]);
	const { identificationNumber, trackingNumbers, hasReceipt, foreignSsn } = values;
	const { identificationNumber: identificationNumberError, trackingNumbers: trackingNumbersError } = errors;

	const identificationNumberChanged = previousIdentificationNumber !== identificationNumber;
	const trackingNumbersChanged = previousTrackingNumbers !== trackingNumbers.length;

	const shouldFetchServices =
		!foreignSsn &&
		identificationNumber.length >= 10 &&
		trackingNumbers.length > 0 &&
		!identificationNumberError &&
		!trackingNumbersError &&
		hasReceipt === 'Yes' &&
		(identificationNumberChanged || trackingNumbersChanged);

	const shouldResetServices =
		(foreignSsn || hasReceipt === 'No' || !!identificationNumberError || !!trackingNumbersError) &&
		deliveryServices.length > 1;

	useEffect(() => {
		if (currentStepIndex === DELIVERY_STEP_INDEX && !shouldShowDeliveryStep(values.instructionsToStaff)) {
			next();
		}
	}, [currentStepIndex, values.instructionsToStaff, next]);

	useEffect(() => {
		if (shouldFetchServices) {
			axios({
				url: `${SEND_RECEIPT_EXPRESS_SERVICES}`,
				params: {
					identificationNumber: identificationNumber,
					language: lang,
					trackingnumbers: trackingNumbers,
				},
			}).then(res => {
				const allServices = [defaultService, ...res.data];
				setDeliveryServices(allServices);
				setPreviousIdentificationNumber(identificationNumber);
				setPreviousTrackingNumbers(trackingNumbers.length);
			});
		} else if (shouldResetServices) {
			setPreviousIdentificationNumber('');
			setPreviousTrackingNumbers(0);
			setDeliveryServices([defaultService]);
			setFieldValue('deliveryService', '0');
		}
	}, [
		shouldFetchServices,
		shouldResetServices,
		identificationNumber,
		trackingNumbers,
		lang,
		setFieldValue,
		defaultService,
	]);

	if (
		currentStepIndex !== DELIVERY_STEP_INDEX &&
		latestStepIndexVisited >= DELIVERY_STEP_INDEX &&
		shouldShowDeliveryStep(values.instructionsToStaff)
	)
		return (
			<InactiveStep
				heading={translations?.inactiveTitles?.delivery || t('inactiveTitles.delivery')}
				onClick={() => goTo(DELIVERY_STEP_INDEX)}
				touched={touched}
				errors={errors}
			/>
		);

	if (currentStepIndex === DELIVERY_STEP_INDEX && !shouldShowDeliveryStep(values.instructionsToStaff)) {
		return null;
	}

	const isStepStateValid = values.deliveryService && !errors.deliveryService;

	if (currentStepIndex === DELIVERY_STEP_INDEX)
		return (
			<FormStepWrapper>
				<FormStepHeading>
					{translations?.formStepTitles?.delivery || t('formStepTitles.delivery')}
				</FormStepHeading>
				<P>{translations?.delivery?.explanation || t('delivery.explanation')}</P>
				<Services services={deliveryServices} values={values} setFieldValue={setFieldValue} />
				<ButtonWrapper>
					<PrevButton
						onClick={e => {
							e.stopPropagation();
							prev();
						}}
						>
						{translations?.prevStep || t('prevStep')}
					</PrevButton>
					<ProceedButton
						type="button"
						onClick={e => {
							e.stopPropagation();
							next();
						}}
						disabled={!isStepStateValid}>
						{translations?.nextStep || t('nextStep')}
					</ProceedButton>
				</ButtonWrapper>
			</FormStepWrapper>
		);

	return null;
}
